import { useMemo, useState } from 'react';
import { FiX } from 'react-icons/fi';
import type { NamedItem, RompArea, Rompimento, RompimentoDraft } from '../../types';
import { Button, ModalBackdrop, TextInput } from '../ui';
import { getAreas, toLocalInput } from '../../lib/rompimentos';
import RompimentoMapEditor from './RompimentoMapEditor';

interface RompimentoModalProps {
  rompimento: Rompimento | null;
  cities: NamedItem[];
  onClose: () => void;
  onSave: (draft: RompimentoDraft) => Promise<void>;
}

export default function RompimentoModal({ rompimento, cities, onClose, onSave }: RompimentoModalProps) {
  const initialAreas = useMemo<RompArea[]>(() => (rompimento ? getAreas(rompimento) : []), [rompimento]);

  const [titulo, setTitulo] = useState(rompimento?.titulo || '');
  const [inicio, setInicio] = useState(
    rompimento?.inicio ? toLocalInput(new Date(rompimento.inicio)) : toLocalInput(new Date())
  );
  const [previsao, setPrevisao] = useState(rompimento?.previsao ? toLocalInput(new Date(rompimento.previsao)) : '');
  const [cidadesSel, setCidadesSel] = useState<string[]>(rompimento?.cidades || []);
  const [bairros, setBairros] = useState((rompimento?.bairros || []).join(', '));
  const [descricao, setDescricao] = useState(rompimento?.descricao || '');
  const [mensagem, setMensagem] = useState(rompimento?.mensagem || '');
  const [areas, setAreas] = useState<RompArea[]>(initialAreas);
  const [saving, setSaving] = useState(false);

  const label = 'text-xs font-semibold text-slate-600';
  const circ = areas.filter((a) => a.tipo === 'circulo').length;
  const poly = areas.length - circ;

  const toggleCidade = (name: string) =>
    setCidadesSel((prev) => (prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name]));

  const submit = async () => {
    if (!titulo.trim()) return window.alert('Informe o título do rompimento.');
    if (!inicio) return window.alert('Informe a data/hora de início.');
    const inicioDate = new Date(inicio);
    if (isNaN(inicioDate.getTime())) return window.alert('Data de início inválida.');
    if (previsao) {
      const prev = new Date(previsao);
      if (isNaN(prev.getTime())) return window.alert('Previsão de normalização inválida.');
      if (prev <= inicioDate) return window.alert('A previsão de normalização deve ser depois do início.');
    }
    if (!areas.length) return window.alert('Marque ao menos uma área afetada no mapa (círculo ou polígono).');

    setSaving(true);
    try {
      await onSave({
        _id: rompimento?._id,
        titulo: titulo.trim(),
        inicio,
        previsao,
        cidades: cidadesSel,
        bairros,
        descricao: descricao.trim(),
        mensagem: mensagem.trim(),
        areas
      });
    } catch (e) {
      window.alert((e as Error).message || 'Erro ao salvar rompimento.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalBackdrop onClose={onClose}>
      <div
        className="flex max-h-[94vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h3 className="text-lg font-bold text-slate-800">{rompimento ? 'Editar Rompimento' : 'Novo Rompimento'}</h3>
          <button onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <FiX size={20} />
          </button>
        </div>

        <div className="grid flex-1 grid-cols-1 gap-4 overflow-y-auto px-5 py-4 lg:grid-cols-2">
          {/* Coluna esquerda: campos */}
          <div className="flex flex-col gap-3">
            <div>
              <label className={label}>Título *</label>
              <TextInput value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Ex: Rompimento Centro — 17/06" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={label}>Início *</label>
                <TextInput type="datetime-local" value={inicio} onChange={(e) => setInicio(e.target.value)} />
              </div>
              <div>
                <label className={label}>Previsão de normalização</label>
                <TextInput type="datetime-local" value={previsao} onChange={(e) => setPrevisao(e.target.value)} />
              </div>
            </div>
            <div>
              <label className={label}>Cidades afetadas</label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {cities.length === 0 && <span className="text-xs text-slate-400">Nenhuma cidade cadastrada (Configurações)</span>}
                {cities.map((c) => {
                  const on = cidadesSel.includes(c.name);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleCidade(c.name)}
                      className={
                        on
                          ? 'rounded-full border border-blue-500 bg-blue-600 px-3 py-1 text-xs font-medium text-white'
                          : 'rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 hover:border-slate-300'
                      }
                    >
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className={label}>Bairros afetados (separados por vírgula)</label>
              <TextInput value={bairros} onChange={(e) => setBairros(e.target.value)} placeholder="Centro, Vila Helena, ..." />
            </div>
            <div>
              <label className={label}>Descrição / observações</label>
              <textarea
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                rows={2}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              />
            </div>
            <div>
              <label className={label}>Mensagem de aviso (WhatsApp) — opcional</label>
              <textarea
                value={mensagem}
                onChange={(e) => setMensagem(e.target.value)}
                rows={3}
                placeholder="Se vazio, geramos um aviso automático a partir dos dados acima."
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              />
            </div>
          </div>

          {/* Coluna direita: mapa */}
          <div className="flex flex-col gap-2">
            <RompimentoMapEditor initialAreas={initialAreas} onChange={setAreas} />
            <p className={areas.length ? 'text-xs font-medium text-emerald-600' : 'text-xs text-slate-500'}>
              {areas.length
                ? `✅ ${areas.length} área${areas.length > 1 ? 's' : ''} marcada${areas.length > 1 ? 's' : ''}` +
                  `${circ ? ` · ${circ} círculo${circ > 1 ? 's' : ''}` : ''}${poly ? ` · ${poly} polígono${poly > 1 ? 's' : ''}` : ''}`
                : 'Use as ferramentas no canto superior esquerdo do mapa: círculo (afeta todos os lados) ou polígono (área direcionada). Pode marcar mais de uma.'}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar Rompimento'}
          </Button>
        </div>
      </div>
    </ModalBackdrop>
  );
}
